import { useMutation } from '@apollo/client';
import { UPDATE_CREATIVE_PACKAGE } from '../../graphql/mutations/creativeMutations';

/**
 * Hook to update an existing Creative Package (name, description, price, etc.).
 * Package–project links (Ausgewählte Arbeiten) are not part of this mutation;
 * the caller (e.g. CreativePackageEdit) syncs them after update via
 * CREATE_PACKAGE_PROJECT_LINK / DELETE_PACKAGE_PROJECT_LINK.
 * @returns {object} - { handleUpdatePackage, loading, error }
 */
export const useUpdateCreativePackage = () => {
  const [updatePackageMutation, { loading, error }] = useMutation(UPDATE_CREATIVE_PACKAGE, {
    refetchQueries: ['GetCreativePackage', 'CreativePackagesByCreativeID', 'GetCreative'],
    awaitRefetchQueries: true
  });

  const sanitizeInput = (input = {}) => {
    const cleaned = { ...input };
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === undefined) {
        delete cleaned[key];
      }
    });
    return cleaned;
  };

  const handleUpdatePackage = async (packageData) => {
    try {
      if (!packageData?.id) {
        throw new Error('Das Paket konnte nicht zugeordnet werden. Bitte lade die Seite neu und versuche es erneut.');
      }

      const input = sanitizeInput({
        id: packageData.id,
        creativeID: packageData.creativeID,
        serviceType: packageData.serviceType,
        name: packageData.name,
        description: packageData.description,
        packageType: packageData.packageType,
        duration: packageData.duration,
        price: packageData.price,
        pricePerHour: packageData.pricePerHour,
        deliverables: packageData.deliverables,
        includedServices: packageData.includedServices,
        deliveryTime: packageData.deliveryTime,
        portfolioImages: packageData.portfolioImages,
        equipment: packageData.equipment,
        isPopular: packageData.isPopular,
        order: packageData.order,
        bookingCount: packageData.bookingCount,
        averageRating: packageData.averageRating,
        isActive: packageData.isActive,
        status: packageData.status
      });

      const variables = sanitizeInput({
        input,
        condition: packageData?.condition
      });

      const { data: packageResponse } = await updatePackageMutation({
        variables
      });

      return packageResponse?.updateCreativePackage;
    } catch (error) {
      console.error('Error updating creative package:', error);
      throw error;
    }
  };

  /**
   * Helper to update package status
   */
  const updatePackageStatus = async (packageId, newStatus, condition) => {
    if (!newStatus) {
      throw new Error('Der Status konnte nicht gesetzt werden. Bitte versuche es erneut.');
    }
    return handleUpdatePackage({
      id: packageId,
      status: newStatus,
      condition
    });
  };

  /**
   * Helper to toggle package active status
   */
  const togglePackageActive = async (packageId, isActive, condition) => {
    return handleUpdatePackage({
      id: packageId,
      isActive: isActive,
      condition
    });
  };

  return {
    handleUpdatePackage,
    updatePackageStatus,
    togglePackageActive,
    loading,
    error
  };
};
